import { BufferGeometry, Group, Line, LineBasicMaterial, Vector3 } from 'three';
// @ts-ignore
import { Text } from 'troika-three-text';

import {
  BaseBurners,
  Burner,
  Color,
  Cupboard,
  LabelName,
  Oven,
  RangeTop,
  Region,
} from '../../shared/Enums';
import { RangeState } from '../../shared/RangeState';

export class Labels {
  /** All range front labels should be added to (or removed from) this group */
  front = new Group();

  /** All range top labels should be added to (or removed from) this group */
  top = new Group();

  frontConfig = {
    leftCupboard: null,
    leftOven: null,
    leftStorageDrawer: null,
    petiteOven: null,
    rightCupboard: null,
    rightOven: null,
    rightStorageDrawer: null,
    verticalOven: null,
  };

  topConfig = {
    farLeft: {
      type: null,
      xOffset: 0,
    },
    left: {
      type: null,
      xOffset: 0,
    },
    center: {
      type: null,
      xOffset: 0,
    },
    right: {
      type: null,
      xOffset: 0,
    },
    farRight: {
      type: null,
      xOffset: 0,
    },
  };

  /**
   * The current configuration of the range
   * @type {RangeState}
   */
  state;

  static get burnerHeight() {
    return 0.95;
  }

  static get frontLabelHeight() {
    return 1.05;
  }

  static get centerLabelHeight() {
    return 1.09;
  }

  static get backLabelHeight() {
    return 1.13;
  }

  static get frontLabelDepth() {
    return 0.07;
  }

  static get centerLabelDepth() {
    return -0.08;
  }

  static get backLabelDepth() {
    return -0.2;
  }

  static get lineGap() {
    return 0.03;
  }

  static get xShift() {
    return 0.05;
  }

  /**
   * @param {RangeState} state
   */
  constructor(state) {
    this.state = state;

    // Create the five regions for range top labels
    const regions = ['farLeft', 'left', 'center', 'right', 'farRight'];
    regions.forEach((region) => {
      const labelsGroup = new Group();
      labelsGroup.name = region;
      this.top.add(labelsGroup);
    });

    // Hide labels by default
    this.front.visible = false;
    this.top.visible = false;
  }

  changeColor() {
    this.front.children.forEach((label) => {
      // @ts-ignore
      label.color = this.#frontLabelColor();
    });
  }

  changeText(objectName, part) {
    // @ts-ignore
    this.front.getObjectByName(objectName).text = this.#textForPart(part);
  }

  addRangeFrontLabels() {
    this.#addLeftOvenLabel();
    this.#addRightOvenLabel();
    this.#addLeftCupboardLabel();
    this.#addRightCupboardLabel();
    this.#addPetiteOvenLabel();
    this.#addVerticalOvenLabel();
    this.#addLeftStorageDrawerLabel();
    this.#addRightStorageDrawerLabel();
  }

  tick() {
    this.top.children.forEach((region) => {
      region.children.forEach((label) => {
        // @ts-ignore
        if (!label.isLine) {
          label.setRotationFromEuler(this.state.camera.rotation);
        }
      });
    });
  }

  /**
   * Add descriptive labels for the base 3 feux burners
   * @param {string} burnersType
   * @param {string} region
   */
  addTopLabels(burnersType, region = Region.center) {
    switch (burnersType) {
      case BaseBurners.threeFeuxClassique:
        this.#add3FeuxLabels(this.topConfig.center.xOffset, RangeTop.classique);
        break;

      case BaseBurners.threeFeuxClassiqueRev:
        this.#add3FeuxRevLabels(
          this.topConfig.center.xOffset,
          RangeTop.classique
        );
        break;

      case BaseBurners.threeFeuxTraditional:
        this.#add3FeuxLabels(
          this.topConfig.center.xOffset,
          RangeTop.traditional
        );
        break;

      case BaseBurners.threeFeuxTraditionalRev:
        this.#add3FeuxRevLabels(
          this.topConfig.center.xOffset,
          RangeTop.traditional
        );
        break;

      case BaseBurners.fourFeuxClassique:
        this.#add4FeuxLabels(this.topConfig.center.xOffset);
        break;

      case BaseBurners.fourFeuxClassiqueSully:
        this.#add4FeuxLabels(this.topConfig.center.xOffset, 'sully');
        break;

      case BaseBurners.fiveFeuxClassique:
        this.#add5FeuxLabels(this.topConfig.center.xOffset, RangeTop.classique);
        break;

      case BaseBurners.fiveFeuxTraditional:
        this.#add5FeuxLabels(
          this.topConfig.center.xOffset,
          RangeTop.traditional
        );
        break;

      case BaseBurners.sixFeuxClassique:
        this.#add6FeuxLabels(this.topConfig.center.xOffset, false);
        break;

      case BaseBurners.sixFeuxClassiqueRev:
        this.#add6FeuxLabels(this.topConfig.center.xOffset, true);
        break;

      case Burner.two11kBurners:
      case Burner.two15kBurners:
      case Burner.twoInductionRings:
        this.#add2FeuxLabels(burnersType, region);
        break;

      case Burner.electricPlancha:
      case Burner.flameGrill:
      case Burner.multiCooker:
      case Burner.one18kBurner:
      case Burner.stainlessSteelWorkstation:
      case Burner.traditionalSimmerPlate:
        this.#add1FeuxLabel(burnersType, region);
        break;
    }
  }

  /**
   * Add a descriptive label for an optional "burner"
   * @param {string} burner
   * @param {string} region
   */
  #add1FeuxLabel(burner, region) {
    const labelsGroup = this.top.getObjectByName(region);
    labelsGroup.clear();

    const xOffset = this.topConfig[region].xOffset;

    const labelPos = [
      xOffset,
      Labels.centerLabelHeight,
      Labels.centerLabelDepth,
    ];
    const label = this.#topLabel(burner, labelPos);
    const line = this.#burnerLine(
      [labelPos[0], labelPos[1] - Labels.lineGap, labelPos[2]],
      [labelPos[0], Labels.burnerHeight, 0]
    );

    labelsGroup.add(label, line);
  }

  /**
   * Add descriptive labels for an optional two burners
   * @param {string} burner
   * @param {string} region
   */
  #add2FeuxLabels(burner, region) {
    const labelsGroup = this.top.getObjectByName(region);
    labelsGroup.clear();

    const xOffset = this.topConfig[region].xOffset;

    const rearPos = [
      xOffset - Labels.xShift,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const rearLabel = this.#topLabel(burner, rearPos);
    const rearLine = this.#burnerLine(
      [rearPos[0], rearPos[1] - Labels.lineGap, rearPos[2]],
      [
        rearPos[0] + Labels.xShift,
        Labels.burnerHeight,
        rearPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const frontPos = [
      xOffset + Labels.xShift,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const frontLabel = this.#topLabel(burner, frontPos);
    const frontLine = this.#burnerLine(
      [frontPos[0], frontPos[1] - Labels.lineGap, frontPos[2]],
      [
        frontPos[0] - Labels.xShift,
        Labels.burnerHeight,
        frontPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    labelsGroup.add(rearLabel, rearLine, frontLabel, frontLine);
  }

  /**
   * Add descriptive labels for the base 3 feux classique/traditional burners
   * @param {number} xOffset
   * @param {string} rangeTop
   */
  #add3FeuxLabels(xOffset, rangeTop) {
    const labelsGroup = this.top.getObjectByName(Region.center);
    labelsGroup.clear();

    const mainBurner =
      rangeTop === RangeTop.classique
        ? Burner.eighteenK
        : Burner.traditionalSimmerPlate;

    const eighteenKPos = [
      xOffset - 0.21,
      Labels.centerLabelHeight,
      Labels.centerLabelDepth,
    ];
    const eighteenKLabel = this.#topLabel(mainBurner, eighteenKPos);
    const eighteenKLine = this.#burnerLine(
      [eighteenKPos[0], eighteenKPos[1] - Labels.lineGap, eighteenKPos[2]],
      [eighteenKPos[0], Labels.burnerHeight, 0]
    );

    const fiveKPos = [
      xOffset - Labels.xShift + 0.145,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const fiveKlabel = this.#topLabel(Burner.fiveK, fiveKPos);
    const fiveKLine = this.#burnerLine(
      [fiveKPos[0], fiveKPos[1] - Labels.lineGap, fiveKPos[2]],
      [
        fiveKPos[0] + Labels.xShift,
        Labels.burnerHeight,
        fiveKPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const elevenKPos = [
      xOffset + Labels.xShift + 0.145,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const elevenKLabel = this.#topLabel(Burner.elevenK, elevenKPos);
    const elevenKLine = this.#burnerLine(
      [elevenKPos[0], elevenKPos[1] - Labels.lineGap, elevenKPos[2]],
      [
        elevenKPos[0] - Labels.xShift,
        Labels.burnerHeight,
        elevenKPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    labelsGroup.add(
      eighteenKLabel,
      eighteenKLine,
      fiveKlabel,
      fiveKLine,
      elevenKLabel,
      elevenKLine
    );
  }

  /**
   * Add descriptive labels for the base 3 feux (reverse) classique/traditional
   * burners
   * @param {number} xOffset
   * @param {string} rangeTop
   */
  #add3FeuxRevLabels(xOffset, rangeTop) {
    const labelsGroup = this.top.getObjectByName(Region.center);
    labelsGroup.clear();

    const fiveKPos = [
      xOffset - Labels.xShift - 0.27,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const fiveKlabel = this.#topLabel(Burner.fiveK, fiveKPos);
    const fiveKLine = this.#burnerLine(
      [fiveKPos[0], fiveKPos[1] - Labels.lineGap, fiveKPos[2]],
      [
        fiveKPos[0] + Labels.xShift,
        Labels.burnerHeight,
        fiveKPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const elevenKPos = [
      xOffset + Labels.xShift - 0.27,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const elevenKLabel = this.#topLabel(Burner.elevenK, elevenKPos);
    const elevenKLine = this.#burnerLine(
      [elevenKPos[0], elevenKPos[1] - Labels.lineGap, elevenKPos[2]],
      [
        elevenKPos[0] - Labels.xShift,
        Labels.burnerHeight,
        elevenKPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    const mainBurner =
      rangeTop === RangeTop.classique
        ? Burner.eighteenK
        : Burner.traditionalSimmerPlate;

    const eighteenKPos = [
      xOffset + 0.075,
      Labels.centerLabelHeight,
      Labels.centerLabelDepth,
    ];
    const eighteenKLabel = this.#topLabel(mainBurner, eighteenKPos);
    const eighteenKLine = this.#burnerLine(
      [eighteenKPos[0], eighteenKPos[1] - Labels.lineGap, eighteenKPos[2]],
      [eighteenKPos[0], Labels.burnerHeight, 0]
    );

    labelsGroup.add(
      fiveKlabel,
      fiveKLine,
      elevenKLabel,
      elevenKLine,
      eighteenKLabel,
      eighteenKLine
    );
  }

  /**
   * Add descriptive labels for the base 4 feux classique burners
   * @param {number} xOffset
   * @param {string} type - 'normal' (wide) or 'sully' (narrow)
   */
  #add4FeuxLabels(xOffset, type = 'normal') {
    const labelsGroup = this.top.getObjectByName(Region.center);
    labelsGroup.clear();

    const leftXOffset = type === 'sully' ? 0.11 : 0;

    const elevenKBackPos = [
      xOffset + leftXOffset - Labels.xShift - 0.27,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const elevenKBackLabel = this.#topLabel(Burner.elevenK, elevenKBackPos);
    const elevenKBackLine = this.#burnerLine(
      [
        elevenKBackPos[0],
        elevenKBackPos[1] - Labels.lineGap,
        elevenKBackPos[2],
      ],
      [
        elevenKBackPos[0] + Labels.xShift,
        Labels.burnerHeight,
        elevenKBackPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const elevenKFrontPos = [
      xOffset + leftXOffset + Labels.xShift - 0.27,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const elevenKFrontLabel = this.#topLabel(Burner.elevenK, elevenKFrontPos);
    const elevenKFrontLine = this.#burnerLine(
      [
        elevenKFrontPos[0],
        elevenKFrontPos[1] - Labels.lineGap,
        elevenKFrontPos[2],
      ],
      [
        elevenKFrontPos[0] - Labels.xShift,
        Labels.burnerHeight,
        elevenKFrontPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    const fiveKPos = [
      xOffset - Labels.xShift + 0.15,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const fiveKlabel = this.#topLabel(Burner.fiveK, fiveKPos);
    const fiveKLine = this.#burnerLine(
      [fiveKPos[0], fiveKPos[1] - Labels.lineGap, fiveKPos[2]],
      [
        fiveKPos[0] + Labels.xShift,
        Labels.burnerHeight,
        fiveKPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const fifteenKPos = [
      xOffset + Labels.xShift + 0.15,
      Labels.frontLabelHeight,
      0.07,
    ];
    const fifteenKLabel = this.#topLabel(Burner.fifteenK, fifteenKPos);
    const fifteenKLine = this.#burnerLine(
      [fifteenKPos[0], fifteenKPos[1] - Labels.lineGap, fifteenKPos[2]],
      [
        fifteenKPos[0] - Labels.xShift,
        Labels.burnerHeight,
        fifteenKPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    labelsGroup.add(
      elevenKBackLabel,
      elevenKBackLine,
      elevenKFrontLabel,
      elevenKFrontLine,
      fiveKlabel,
      fiveKLine,
      fifteenKLabel,
      fifteenKLine
    );
  }

  /**
   * Add descriptive labels for the base 5 feux classique/traditional burners
   * @param {number} xOffset
   * @param {string} rangeTop
   */
  #add5FeuxLabels(xOffset, rangeTop) {
    const labelsGroup = this.top.getObjectByName(Region.center);
    labelsGroup.clear();

    const mainBurner =
      rangeTop === RangeTop.classique
        ? Burner.eighteenK
        : Burner.traditionalSimmerPlate;

    const elevenKBackPos = [
      xOffset - Labels.xShift - 0.35,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const elevenKBackLabel = this.#topLabel(Burner.elevenK, elevenKBackPos);
    const elevenKBackLine = this.#burnerLine(
      [
        elevenKBackPos[0],
        elevenKBackPos[1] - Labels.lineGap,
        elevenKBackPos[2],
      ],
      [
        elevenKBackPos[0] + Labels.xShift,
        Labels.burnerHeight,
        elevenKBackPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const elevenKFrontPos = [
      xOffset + Labels.xShift - 0.35,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const elevenKFrontLabel = this.#topLabel(Burner.elevenK, elevenKFrontPos);
    const elevenKFrontLine = this.#burnerLine(
      [
        elevenKFrontPos[0],
        elevenKFrontPos[1] - Labels.lineGap,
        elevenKFrontPos[2],
      ],
      [
        elevenKFrontPos[0] - Labels.xShift,
        Labels.burnerHeight,
        elevenKFrontPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    const eighteenKPos = [
      xOffset,
      Labels.centerLabelHeight,
      Labels.centerLabelDepth,
    ];
    const eighteenKLabel = this.#topLabel(mainBurner, eighteenKPos);
    const eighteenKLine = this.#burnerLine(
      [eighteenKPos[0], eighteenKPos[1] - Labels.lineGap, eighteenKPos[2]],
      [eighteenKPos[0], Labels.burnerHeight, 0]
    );

    const fiveKPos = [
      xOffset - Labels.xShift + 0.35,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const fiveKLabel = this.#topLabel(Burner.fiveK, fiveKPos);
    const fiveKLine = this.#burnerLine(
      [fiveKPos[0], fiveKPos[1] - Labels.lineGap, fiveKPos[2]],
      [
        fiveKPos[0] + Labels.xShift,
        Labels.burnerHeight,
        fiveKPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const fifteenKPos = [
      xOffset + Labels.xShift + 0.35,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const fifteenKLabel = this.#topLabel(Burner.fifteenK, fifteenKPos);
    const fifteenKLine = this.#burnerLine(
      [fifteenKPos[0], fifteenKPos[1] - Labels.lineGap, fifteenKPos[2]],
      [
        fifteenKPos[0] - Labels.xShift,
        Labels.burnerHeight,
        fifteenKPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    labelsGroup.add(
      elevenKBackLabel,
      elevenKBackLine,
      elevenKFrontLabel,
      elevenKFrontLine,
      eighteenKLabel,
      eighteenKLine,
      fiveKLabel,
      fiveKLine,
      fifteenKLabel,
      fifteenKLine
    );
  }

  /**
   * Add descriptive labels for the base 6 feux classique burners
   * @param {number} xOffset
   * @param {boolean} reverse
   */
  #add6FeuxLabels(xOffset, reverse) {
    const labelsGroup = this.top.getObjectByName(Region.center);
    labelsGroup.clear();

    const sideFrontBurner = reverse ? Burner.elevenK : Burner.fiveK;
    const sideBackBurner = reverse ? Burner.fiveK : Burner.elevenK;

    const leftBackPos = [
      xOffset - Labels.xShift - 0.29,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const fiveKLeftLabel = this.#topLabel(sideBackBurner, leftBackPos);
    const fiveKLeftLine = this.#burnerLine(
      [leftBackPos[0], leftBackPos[1] - Labels.lineGap, leftBackPos[2]],
      [
        leftBackPos[0] + Labels.xShift,
        Labels.burnerHeight,
        leftBackPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const leftFrontPos = [
      xOffset + Labels.xShift - 0.29,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const elevenKLeftLabel = this.#topLabel(sideFrontBurner, leftFrontPos);
    const elevenKLeftLine = this.#burnerLine(
      [leftFrontPos[0], leftFrontPos[1] - Labels.lineGap, leftFrontPos[2]],
      [
        leftFrontPos[0] - Labels.xShift,
        Labels.burnerHeight,
        leftFrontPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    const elevenKCenterPos = [
      xOffset - Labels.xShift,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const elevenKCenterLabel = this.#topLabel(Burner.elevenK, elevenKCenterPos);
    const elevenKCenterLine = this.#burnerLine(
      [
        elevenKCenterPos[0],
        elevenKCenterPos[1] - Labels.lineGap,
        elevenKCenterPos[2],
      ],
      [
        elevenKCenterPos[0] + Labels.xShift,
        Labels.burnerHeight,
        elevenKCenterPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const fifteenKCenterPos = [
      xOffset + Labels.xShift,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const fifteenKCenterLabel = this.#topLabel(
      Burner.fifteenK,
      fifteenKCenterPos
    );
    const fifteenKCenterLine = this.#burnerLine(
      [
        fifteenKCenterPos[0],
        fifteenKCenterPos[1] - Labels.lineGap,
        fifteenKCenterPos[2],
      ],
      [
        fifteenKCenterPos[0] - Labels.xShift,
        Labels.burnerHeight,
        fifteenKCenterPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    const rightBackPos = [
      xOffset - Labels.xShift + 0.285,
      Labels.backLabelHeight,
      Labels.backLabelDepth,
    ];
    const fiveKRightLabel = this.#topLabel(sideBackBurner, rightBackPos);
    const fiveKRightLine = this.#burnerLine(
      [rightBackPos[0], rightBackPos[1] - Labels.lineGap, rightBackPos[2]],
      [
        rightBackPos[0] + Labels.xShift,
        Labels.burnerHeight,
        rightBackPos[2] - Labels.backLabelDepth - 0.13,
      ]
    );

    const rightFrontPos = [
      xOffset + Labels.xShift + 0.285,
      Labels.frontLabelHeight,
      Labels.frontLabelDepth,
    ];
    const elevenKRightLabel = this.#topLabel(sideFrontBurner, rightFrontPos);
    const elevenKRightLine = this.#burnerLine(
      [rightFrontPos[0], rightFrontPos[1] - Labels.lineGap, rightFrontPos[2]],
      [
        rightFrontPos[0] - Labels.xShift,
        Labels.burnerHeight,
        rightFrontPos[2] + Labels.frontLabelDepth + 0.01,
      ]
    );

    labelsGroup.add(
      fiveKLeftLabel,
      fiveKLeftLine,
      elevenKLeftLabel,
      elevenKLeftLine,
      elevenKCenterLabel,
      elevenKCenterLine,
      fifteenKCenterLabel,
      fifteenKCenterLine,
      fiveKRightLabel,
      fiveKRightLine,
      elevenKRightLabel,
      elevenKRightLine
    );
  }

  /**
   * Add descriptive labels for 2 15K optional burners
   * @param {array} position
   */
  addTwo15KBurnersLabels(position) {
    const fiveKPos = [position[0], position[1], position[2]];
    this.#addBurnerLabel(fiveKPos, Burner.fiveK);
    this.#burnerLine(
      [fiveKPos[0], fiveKPos[1] - 0.05, fiveKPos[2]],
      [fiveKPos[0] + 0.05, fiveKPos[1] - 0.23, fiveKPos[2]]
    );
  }

  /**
   * Draw a line from the label to the burner
   * @param {array} from
   * @param {array} to
   * @returns {Line}
   */
  #burnerLine(from, to) {
    const fromVector = new Vector3(...from);
    const toVector = new Vector3(...to);
    const material = new LineBasicMaterial({ color: 0xffffff, linewidth: 2 });
    const geometry = new BufferGeometry().setFromPoints([fromVector, toVector]);

    return new Line(geometry, material);
  }

  /**
   * Add a label for the 18k burner
   * @param {array} position
   * @param {string} burner
   */
  #addBurnerLabel(position, burner) {
    const burnerLabel = this.#topLabel(this.#textForPart(burner), position);

    this.top.add(burnerLabel);
  }

  /**
   * The text to be used for the various labels
   * @param {string} rangePart
   * @returns {string}
   */
  #textForPart(rangePart) {
    const partDescriptions = {
      [Burner.eighteenK]: '18,000 BTU',
      [Burner.electricPlancha]: 'Electric Plancha',
      [Burner.elevenK]: '11,000 BTU',
      [Burner.fifteenK]: '15,000 BTU',
      [Burner.fiveK]: '5,000 BTU',
      [Burner.flameGrill]: 'Flame Grill',
      [Burner.multiCooker]: 'Multi-Cooker',
      [Burner.one18kBurner]: '18,000 BTU',
      [Burner.stainlessSteelWorkstation]: `Stainless Steel\nWork Station`,
      [Burner.traditionalSimmerPlate]: `French Plate\nw/ 18,000 BTU`,
      [Burner.two11kBurners]: '11,000 BTU',
      [Burner.two15kBurners]: '15,000 BTU',
      [Burner.twoInductionRings]: 'Induction ring',
      [Cupboard.storage]: 'Storage Cupboard',
      [Cupboard.warming]: 'Warming Cupboard',
      [Oven.convection]: `Electric Convection Oven\nw/ broiler`,
      [Oven.convectionAlt]: `Electric Convection Oven\nw/ broiler`,
      [Oven.electric]: `Electric Oven\nw/ broiler`,
      [Oven.gas]: `Gas Oven\nw/o broiler`,
      [Oven.leftVertConv]: `Vertical Electric Convection Oven\nw/ broiler`,
      [Oven.petite]: `Petit Oven\nw/ broiler`,
      [Oven.rightVertConv]: `Vertical Electric Convection Oven\nw/ broiler`,
      [Oven.vertConv]: `Vertical Electric Convection Oven\nw/ broiler`,
    };

    return partDescriptions[rangePart];
  }

  /**
   * Create an SDF texture to label a part of the range
   * @param {string} burner
   * @param {array} position
   * @returns {Text}
   */
  #topLabel(burner, position) {
    const label = new Text();
    label.text = this.#textForPart(burner);
    label.font = '/assets/fonts/roboto-v47-latin-regular.woff';
    label.fontSize = 0.043;
    label.lineHeight = 1.25;
    label.position.set(...position);
    label.rotation.x = -Math.PI / 2; // rotate to be flat on the range
    label.color = 0x444444;
    label.outlineColor = 0xffffff;
    label.outlineWidth = '10%';
    label.anchorX = 'center';
    label.anchorY = 'middle';
    label.textAlign = 'center';
    label.maxWidth = 0.3;

    label.sync();

    return label;
  }

  #addLeftOvenLabel() {
    const ovenLabel = this.#addFrontLabel(
      this.#textForPart(this.state.leftOven),
      LabelName.leftOven,
      this.frontConfig.leftOven.position,
      this.frontConfig.leftOven.width,
      this.#frontLabelColor()
    );

    this.front.add(ovenLabel);
  }

  #addRightOvenLabel() {
    if (this.frontConfig.rightOven === null) return;

    const ovenLabel = this.#addFrontLabel(
      this.#textForPart(this.state.rightOven),
      LabelName.rightOven,
      this.frontConfig.rightOven.position,
      this.frontConfig.rightOven.width,
      this.#frontLabelColor()
    );

    this.front.add(ovenLabel);
  }

  #addLeftCupboardLabel() {
    if (this.frontConfig.leftCupboard === null) return;

    const cupboard = this.#addFrontLabel(
      this.#textForPart(this.state.leftCupboard),
      LabelName.leftCupboard,
      this.frontConfig.leftCupboard.position,
      this.frontConfig.leftCupboard.width,
      this.#frontLabelColor()
    );

    this.front.add(cupboard);
  }

  #addRightCupboardLabel() {
    if (this.frontConfig.rightCupboard === null) return;

    const cupboard = this.#addFrontLabel(
      this.#textForPart(this.state.rightCupboard),
      LabelName.rightCupboard,
      this.frontConfig.rightCupboard.position,
      this.frontConfig.rightCupboard.width,
      this.#frontLabelColor()
    );

    this.front.add(cupboard);
  }

  #addPetiteOvenLabel() {
    if (this.frontConfig.petiteOven === null) return;

    const ovenLabel = this.#addFrontLabel(
      this.#textForPart(Oven.petite),
      LabelName.rightOven,
      this.frontConfig.petiteOven.position,
      this.frontConfig.petiteOven.width,
      this.#frontLabelColor()
    );

    this.front.add(ovenLabel);
  }

  #addVerticalOvenLabel() {
    if (this.frontConfig.verticalOven === null) return;

    const ovenLabel = this.#addFrontLabel(
      this.#textForPart(Oven.rightVertConv),
      LabelName.verticalConvection,
      this.frontConfig.verticalOven.position,
      this.frontConfig.verticalOven.width,
      this.#frontLabelColor()
    );

    this.front.add(ovenLabel);
  }

  #addLeftStorageDrawerLabel() {
    if (this.frontConfig.leftStorageDrawer === null) return;

    const drawerLabel = this.#addFrontLabel(
      'Storage Drawer',
      LabelName.storageDrawer,
      this.frontConfig.leftStorageDrawer.position,
      this.frontConfig.leftStorageDrawer.width,
      this.#frontLabelColor()
    );

    this.front.add(drawerLabel);
  }

  #addRightStorageDrawerLabel() {
    if (this.frontConfig.rightStorageDrawer === null) return;

    const drawerLabel = this.#addFrontLabel(
      'Storage Drawer',
      LabelName.storageDrawer,
      this.frontConfig.rightStorageDrawer.position,
      this.frontConfig.rightStorageDrawer.width,
      this.#frontLabelColor()
    );

    this.front.add(drawerLabel);
  }

  /**
   * Create an SDF texture to label a part of the range
   * @param {string} text
   * @param {string} name
   * @param {array} position
   * @param {number} maxWidth
   * @param {string|number} color
   * @returns {Text}
   */
  #addFrontLabel(text, name, position, maxWidth, color) {
    const label = new Text();
    label.name = name;
    label.text = text;
    label.font = '/assets/fonts/roboto-v47-latin-regular.woff';
    label.fontSize = 0.043;
    label.lineHeight = 1.25;
    label.position.set(...position);
    label.color = color;
    label.anchorX = 'center';
    label.anchorY = 'middle';
    label.textAlign = 'center';
    label.maxWidth = maxWidth;

    label.sync();

    return label;
  }

  #frontLabelColor() {
    const lightColors = [
      Color.chantilly,
      Color.englishCreme,
      Color.faience,
      Color.ivory,
      Color.provenceYellow,
      Color.roseQuartz,
      Color.stainlessSteel,
      Color.tilleul,
      Color.vertSilice,
      Color.white,
    ];

    const midColors = [
      Color.armor,
      Color.coralBlue,
      Color.delftBlue,
      Color.frangipane,
      Color.mandarine,
      Color.vertSologne,
    ];

    if (lightColors.includes(this.state.color)) {
      return 0x444444;
    } else if (midColors.includes(this.state.color)) {
      return 0xdddddd;
    } else {
      return 0x999999;
    }
  }
}
